/**
 * Esconder um BLOCO durante a apresentação — e por que isto deixou de ser `style.visibility`.
 *
 * ═══ O defeito, medido no `dev` em 2026-08-29 ══════════════════════════════════════════════
 *
 * A primeira versão escrevia `host.style.visibility = 'hidden'` no elemento de bloco (nota,
 * imagem, embed). Funcionava — até o viewport mexer. Medido, com a apresentação no passo 0:
 *
 *     passo 0 (tudo oculto)          ocultos=6   nota(bloco) visivel = false
 *     depois de zoom 1.6x            ocultos=6   nota(bloco) visivel = false
 *     depois de pan                  ocultos=6   nota(bloco) visivel = false
 *     de volta ao enquadramento      ocultos=5   nota(bloco) visivel = TRUE   ← sozinha
 *
 * A nota **reapareceu no meio da apresentação**, sem ninguém avançar passo nenhum.
 *
 * A causa é que `style.visibility` de um bloco **já tem dono**: `updateBlockVisibility`
 * (`framework/std/src/view/element/gfx-block-component.ts:41`) a reescreve toda vez que o
 * `transformState$` muda — que é o que acontece quando o bloco entra ou sai da área ativa do
 * viewport. Nós escrevíamos `hidden`, o upstream escrevia `visible` por cima, e **nada
 * falhava**: os dois estavam fazendo exatamente o que cada um foi escrito para fazer.
 *
 * É a Regra 8.49 do `CLAUDE.md` noutro traje — lá o cliente do Affine "consertava" um `Y.Doc`
 * que não entendia; aqui o renderer dele reafirma uma propriedade que é dele. Nos dois casos o
 * produtor reporta sucesso e o estrago acontece **depois**.
 *
 * ═══ Por que CLASSE com `!important`, e não outra propriedade ══════════════════════════════
 *
 * Trocar para `opacity` ou `display` sairia da colisão e criaria outra pergunta ("quem mais
 * escreve isto?"). A saída que **não disputa** é não escrever no atributo `style`: uma classe
 * nossa, com uma regra `!important` numa folha de estilo. Na cascata, `!important` de folha de
 * autor vence declaração inline não-`!important` — então o upstream continua escrevendo o que
 * sempre escreveu, sem nada mudar do lado dele, e a nossa regra ganha enquanto a classe está
 * lá. Ao restaurar, tiramos a classe e **o valor volta a ser inteiramente dele** — não
 * precisamos mais lembrar o que ele tinha antes, o que era a outra metade frágil do desenho.
 *
 * ⚠️ **Sem o `!important` isto não funciona nem uma vez** — o estilo inline vence a classe, e
 * o bloco nunca esconderia. Há gate para isso.
 *
 * ═══ Por que arquivo separado ══════════════════════════════════════════════════════════════
 *
 * Igual a `passos.ts` e `rotulo.ts`: sem `import` de blocksuite, o núcleo **executa** no
 * `node:test` com um elemento de mentira. A regra 8.52 registra o que custa testar registro
 * por `import` em vez de por efeito.
 */

/** A classe que esconde. Some do elemento ao restaurar. */
export const CLASSE_OCULTO = 'tuninho-passo-oculto';

/** O `id` da folha de estilo, para não injetar duas vezes na mesma raiz. */
export const ID_DA_REGRA = 'tuninho-passos-de-apresentacao-estilo';

/**
 * A regra, e o `!important` dela é a entrega inteira deste arquivo.
 *
 * `visibility` e não `display: none`: o bloco continua ocupando o lugar, então revelar não
 * reflui o quadro. É o mesmo que o viewport do upstream usa para cull.
 */
export const REGRA = `.${CLASSE_OCULTO}{visibility:hidden !important;}`;

/** A raiz onde o bloco vive — `document` na árvore medida, `ShadowRoot` se um dia mudar. */
export interface RaizDeEstilo {
  getElementById(id: string): unknown;
  head?: { appendChild(no: unknown): unknown } | null;
  appendChild(no: unknown): unknown;
}

/** O mínimo que precisamos do elemento de bloco. */
export interface HostDeBloco {
  classList: { toggle(classe: string, forcar: boolean): unknown };
  getRootNode(): RaizDeEstilo;
}

/** Cria o nó de estilo. Injetado para o núcleo não conhecer `document`. */
export type CriarEstilo = (id: string, css: string) => unknown;

/**
 * Põe a regra na raiz, uma vez só. Devolve `true` quando de fato injetou.
 *
 * Idempotente de propósito: o controlador chama isto a cada elemento escondido, e a raiz é a
 * mesma para todos. Contar com "chamar uma vez no `mounted`" seria depender de o editor não
 * ser remontado — e é justamente a remontagem que a Regra 8.53 mostra ser fácil de esquecer.
 */
export function garantirRegra(raiz: RaizDeEstilo, criar: CriarEstilo): boolean {
  if (raiz.getElementById(ID_DA_REGRA)) return false;
  const no = criar(ID_DA_REGRA, REGRA);
  const destino = raiz.head ?? raiz;
  destino.appendChild(no);
  return true;
}

/**
 * Esconde ou mostra um bloco. `oculto = false` devolve o controle ao upstream por inteiro.
 */
export function ocultarBloco(
  host: HostDeBloco,
  oculto: boolean,
  criar: CriarEstilo
): void {
  // A regra só precisa existir quando alguém vai esconder — injetar ao revelar poria uma folha
  // de estilo em toda árvore que só passou por aqui para restaurar.
  if (oculto) garantirRegra(host.getRootNode(), criar);
  host.classList.toggle(CLASSE_OCULTO, oculto);
}
